






















































import { ISbStoryData } from '@storyblok/js/dist/types';
import { PropType } from 'vue';
import { defineComponent, useMeta } from '@nuxtjs/composition-api';
import { SHOP_TITLE } from '~/config/base';

export interface IBlokSingleImage extends ISbStoryData {
  _uid: string;
  SingleImage?: {
    filename?: string;
  };
  SingleImageTablet?: {
    filename?: string;
  };
  SingleImageMobile?: {
    filename?: string;
  };
  filename?: string;
  Alt?: string;
  showAlt?: boolean;
  BorderRadiusTopLeft?: number;
  BorderRadiusTopRight?: number;
  BorderRadiusBottomLeft?: number;
  BorderRadiusBottomRight?: number;
  LazyLoad?: boolean;
  Preload?: boolean;
  desktopWidth?: number;
  tabletWidth?: number;
  mobileWidth?: number;
  Width?: number;
  Height?: number;
}

export default defineComponent({
  name: 'SingleImage',
  props: {
    blok: {
      type: Object as PropType<IBlokSingleImage>,
      required: true,
    },
  },

  setup({ blok }: { blok: IBlokSingleImage }) {
    const breakpoints = {
      desktop: 1024,
      tablet: 768,
      mobile: 320,
    };
    const deviceMedia = {
      desktop: `(min-width:${breakpoints.desktop}px)`,
      tablet: `(min-width:${breakpoints.tablet}px)`,
      mobile: '(min-width: 1px)',
    };

    const {
      mobileWidth,
      tabletWidth,
      desktopWidth,
      Width: width,
      Height: height,
      Preload: preload,
      LazyLoad: lazyLoad,
    } = blok;

    const alt = blok.Alt ? blok.Alt : SHOP_TITLE;
    const showAlt = blok.showAlt ? blok.showAlt : false;

    const maxWidth = { 'max-width': width ? `${width}px` : '' };
    const maxHeight = { 'max-height': height ? `${height}px` : '' };
    const borderRadiusTopLeftStyle = blok.BorderRadiusTopLeft
      ? { 'border-top-left-radius': `${blok.BorderRadiusTopLeft}px` }
      : {};
    const borderRadiusTopRightStyle = blok.BorderRadiusTopRight
      ? { 'border-top-right-radius': `${blok.BorderRadiusTopRight}px` }
      : {};
    const borderRadiusBottomLeftStyle = blok.BorderRadiusBottomLeft
      ? { 'border-bottom-left-radius': `${blok.BorderRadiusBottomLeft}px` }
      : {};
    const borderRadiusBottomRightStyle = blok.BorderRadiusBottomRight
      ? { 'border-bottom-right-radius': `${blok.BorderRadiusBottomRight}px` }
      : {};

    const imageWrapperWidth = blok.BorderRadiusTopLeft
      || blok.BorderRadiusTopRight
      || blok.BorderRadiusBottomLeft
      || blok.BorderRadiusBottomRight
      ? { width: 'fit-content !important', margin: '0 auto' }
      : { width: '100% !important' };

    const optimizedFilename = (filename: string, width: number) => {
      if (filename.endsWith('.svg') && width) return `${filename}/m/`;
      if (!filename.endsWith('.svg')) return `${filename}/m/`;
      return filename;
    };

    const prepareQuery = (
      filename: string,
      fallbackFilename: string,
      width: number,
    ) => {
      const resultFilename = filename || fallbackFilename;
      const size = width ? `${width}x0` : '';
      return `${optimizedFilename(resultFilename, width)}${size}`;
    };

    const prepareImageSources = () => {
      const filenameDesktop = blok.SingleImage
        ? blok.SingleImage.filename
        : blok.filename;
      const filenameTablet = blok.SingleImageTablet
        ? blok.SingleImageTablet.filename
        : blok.filename;
      const filenameMobile = blok.SingleImageMobile
        ? blok.SingleImageMobile.filename
        : blok.filename;

      const sources: any = {
        default: {
          srcset: optimizedFilename(filenameDesktop, desktopWidth),
        },
      };

      sources.desktop = {
        media: deviceMedia.desktop,
        srcset: prepareQuery(filenameDesktop, null, desktopWidth),
      };
      sources.tablet = {
        media: deviceMedia.tablet,
        srcset: prepareQuery(filenameTablet, filenameDesktop, tabletWidth),
      };
      sources.mobile = {
        media: deviceMedia.mobile,
        srcset: prepareQuery(filenameMobile, filenameDesktop, mobileWidth),
      };

      return sources;
    };

    const addPreloadLink = () => {
      if (!preload) return {};

      const link: any = [];
      if (imageSources.desktop) {
        link.push({
          rel: 'preload',
          as: 'image',
          href: imageSources.desktop.srcset,
          media: deviceMedia.desktop,
        });
      }
      if (imageSources.tablet) {
        link.push({
          rel: 'preload',
          as: 'image',
          href: imageSources.tablet.srcset,
          media: deviceMedia.tablet,
        });
      }
      if (imageSources.mobile) {
        link.push({
          rel: 'preload',
          as: 'image',
          href: imageSources.mobile.srcset,
          media: deviceMedia.mobile,
        });
      }
      if (
        !imageSources.mobile
        && !imageSources.tablet
        && !imageSources.desktop
      ) {
        link.push({
          rel: 'preload',
          as: 'image',
          href: imageSources.default.srcset,
        });
      }
      return { link };
    };

    const imageSources = prepareImageSources();
    useMeta(() => addPreloadLink());

    return {
      alt,
      width,
      height,
      showAlt,
      lazyLoad,
      maxWidth,
      maxHeight,
      imageSources,
      imageWrapperWidth,
      borderRadiusTopLeftStyle,
      borderRadiusTopRightStyle,
      borderRadiusBottomLeftStyle,
      borderRadiusBottomRightStyle,
    };
  },
  head: {},
});
